/**
 * SC-476: what Safari's picture-in-picture `<video>` plays, and the only writer of its real `srcObject`.
 *
 * **Why LiveKit may not hold the element's real stream.** The element has to stay registered with LiveKit
 * (`track.attach(element)`): adaptive stream sizes the subscription from it, and counts the element that is
 * `document.pictureInPictureElement` as visible, which is the only thing that keeps someone's video flowing
 * while the Sevv tab is hidden (`pauseVideoInBackground` is on, and manual `setEnabled` is overridden by
 * adaptive stream). But everything LiveKit does to a registered element goes through `element.srcObject`:
 * `RemoteVideoTrack.setMuted` detaches every attached element the moment a remote publication is muted
 * (someone's camera off, or held for a background), and an unsubscribe does the same, and `detachTrack`
 * answers an emptied stream with `srcObject = null`. Safari closes the window on that: measured 2026-10-24,
 * `detachTrack` nulls the source inside LiveKit's own signal handler, before any model update of ours can run,
 * `emptied` fires and `webkitpresentationmodechanged` follows 2 ms later.
 *
 * So `video.srcObject` is made to read as a stream of LiveKit's own (the facade): it can attach, detach,
 * empty and null it as it likes and the element does not notice. The element plays a second stream that only
 * this class touches. A track LiveKit adds to the facade (our own attach, or LiveKit re-attaching after a
 * restart) is mirrored into the real stream; a track LiveKit removes is not, because removal is what empties
 * the element, and the renderer decides what replaces a track. The real `srcObject` is therefore assigned
 * once, when something is first shown, and set to `null` only by `clear()`.
 *
 * Add before remove for the same reason: the real stream never has no video track between two pictures.
 */

export class PipElementSource {
  /** What LiveKit reads and writes as the element's `srcObject`. Null where there is no `MediaStream`. */
  private readonly facade: MediaStream | null
  /** What the element plays. Created with the first track shown and kept across `clear()`. */
  private stream: MediaStream | null = null
  private assigned = false
  private readonly writeReal: (value: MediaStream | null) => void

  constructor(private readonly video: HTMLVideoElement) {
    const native = Object.getOwnPropertyDescriptor(HTMLMediaElement.prototype, 'srcObject')
    // jsdom has no `srcObject`: there the real source is only ever this class's own field.
    this.writeReal = native?.set ? (value) => native.set!.call(video, value) : () => {}

    this.facade = typeof MediaStream === 'function' ? new MediaStream() : null
    const facade = this.facade
    if (facade) {
      const add = facade.addTrack.bind(facade)
      const remove = facade.removeTrack.bind(facade)
      facade.addTrack = (track: MediaStreamTrack) => {
        add(track)
        if (track.kind === 'video') this.show(track)
      }
      facade.removeTrack = (track: MediaStreamTrack) => {
        remove(track)
      }
    }

    Object.defineProperty(video, 'srcObject', {
      configurable: true,
      get: () => this.facade,
      // Ignored: whatever LiveKit assigns, `null` included, is not what the element plays.
      set: () => {},
    })
  }

  /** What the element is playing, which is not `video.srcObject`. For the tests and for diagnostics. */
  get displayed(): MediaStream | null {
    return this.assigned ? this.stream : null
  }

  /** Puts `media` on the element: in before the others go out, and assigned only the first time. */
  show(media: MediaStreamTrack | undefined): void {
    if (!media || typeof MediaStream !== 'function') return
    const stream = (this.stream ??= new MediaStream())
    if (!stream.getTracks().includes(media)) stream.addTrack(media)
    stream.getVideoTracks().forEach((other) => {
      if (other !== media) stream.removeTrack(other)
    })
    if (!this.assigned) {
      this.assigned = true
      this.writeReal(stream)
    }
  }

  /** Nothing to show and nobody to close a window for: the one place the real source is emptied. */
  clear(): void {
    this.stream?.getTracks().forEach((track) => this.stream?.removeTrack(track))
    if (this.assigned) this.writeReal(null)
    this.assigned = false
  }

  /** Gives the element its own `srcObject` back; the element is about to be removed. */
  destroy(): void {
    this.clear()
    delete (this.video as { srcObject?: unknown }).srcObject
  }
}
